import { BaseAnalyticsProvider } from '@common/analytics-layer/providers/base.provider'
import { AnalyticsEvents } from '@common/analytics-layer/types/events'
import { useGroupsUIStore } from '@communities/groups-layer/stores/groups.store'
import type { AnalyticsEventProperties } from '@common/analytics-layer/types/properties'

import { resolveTrafficPageId, trackCommunityTrafficPageView } from '../utils/community-traffic-page-view.utils'
import { resolveCommunityGroupSlugFromPath } from '../utils/resolve-community-group-route'

interface CommunityTrafficPayload {
  pageId: string
  groupId: string
  locationId: string
  pageUrl: string
}

/**
 * Creator Traffic KPI ingest — fans out from the common PAGE_VIEW pipeline to
 * `/community-analytics/.../traffic/page-view`.
 */
export class CommunityTrafficAnalyticsProvider extends BaseAnalyticsProvider<CommunityTrafficPayload> {
  readonly name = 'CommunityTrafficAnalytics'

  transform(event: string, properties: AnalyticsEventProperties): CommunityTrafficPayload | null {
    if (event !== AnalyticsEvents.PAGE_VIEW) return null

    const pagePath = (properties.pagePath as string) || (properties.route as string) || ''
    if (!pagePath.trim()) return null

    const routeSlug = resolveCommunityGroupSlugFromPath(pagePath)
    if (!routeSlug) return null

    const groupsStore = useGroupsUIStore()
    if (groupsStore.isCurrentGroupLoading) return null

    const group = groupsStore.currentGroup
    const groupId = group?._id?.trim()
    if (!groupId || group?.slug !== routeSlug) return null

    const pageId = resolveTrafficPageId(pagePath)
    if (!pageId) return null

    const locationId = group.locationId?.trim() || (properties.locationId as string | undefined)?.trim()
    if (!locationId) return null

    return {
      pageId,
      groupId,
      locationId,
      pageUrl: pagePath.trim(),
    }
  }

  send(payload: CommunityTrafficPayload): void {
    trackCommunityTrafficPageView(payload)
  }
}
